import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BUBBLE_SHOWN, bubbleText, bubbleParts, bubbleNudge } from '../../utils/roomPeek';
import { LIMITS } from '../../constants/gameLimits';

/**
 * 💬 09-26 option C: THE SCENE'S CHAT AS SPEECH BUBBLES (Nina: "I really like
 * C, but only for certain events"; mockup https://claude.ai/artifact/FRhcSrwicgTVVW62KKdpFs).
 * On an event's own scene (RoomPeekLightbox `chat`) the newest lines float up
 * over the room as bubbles and older ones fade; a message box sits on the
 * picture; "Show all N messages" opens the whole list without leaving the scene.
 * Which events, and which lines, is pure (roomPeek.sceneBubblesOn /
 * sceneChatFor); this module only draws and sends.
 *   · the box sends through the page's own send (handleInterjectionAndPlay,
 *     the Wish box's path), one send in flight at a time
 *   · reduced motion: a still stack, nothing floats
 *   · bubbles never take a pointer: the painting under them stays clickable
 * ⭐ This frontend has NO global box-sizing: every padded box sets border-box.
 * Player copy: short, punchy, no em dashes.
 */
const PARCH = '#e6dac6';
const GOLD = '#e9b565';
const LINE = 'rgba(233,181,101,0.45)';
const INK = 'rgba(20,14,9,0.86)';
const AI_NAME = '#9fd3e6';     // every AI shares one cyan (the house's art rule)
const PERSON_NAME = '#f0ac55'; // people are gold
const RANK_OPACITY = [1, 0.78, 0.5]; // newest → oldest shown (the mockup's three)
const FLOAT_MS = 420;
const SAY_MAX = LIMITS.INTERJECTION_MAX;
const stop = (e) => e.stopPropagation();

/** Where the scene goes full screen: phones, and tablets held upright (there
 *  the fitted picture is a thin strip in a sea of black). Landscape keeps the
 *  fitted picture, which already fills the width. */
export const STAGE_QUERY = '(max-width: 767px), (orientation: portrait) and (max-width: 1180px)';
/** Live screen check (jsdom has no matchMedia: wide). The full-screen scene
 *  keys off the screen, not the device: the picture fills the window. */
export function useNarrow(query = STAGE_QUERY) {
  const get = () => !!(typeof window !== 'undefined' && window.matchMedia && window.matchMedia(query).matches);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const m = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null;
    if (!m) return undefined;
    const on = () => setNarrow(!!m.matches);
    on();
    if (m.addEventListener) m.addEventListener('change', on); else if (m.addListener) m.addListener(on);
    return () => { if (m.removeEventListener) m.removeEventListener('change', on); else if (m.removeListener) m.removeListener(on); };
  }, [query]);
  return narrow;
}

function Words({ text }) {
  return bubbleParts(text).map((p, i) => (p.i
    ? <i key={i} style={{ opacity: 0.85 }}>{p.t}</i>
    : <React.Fragment key={i}>{p.t}</React.Fragment>));
}
function Name({ line }) {
  return <b style={{ color: line.ai ? AI_NAME : PERSON_NAME, fontWeight: 700 }}>{line.name}</b>;
}

/** The newest BUBBLE_SHOWN lines as bubbles, newest nearest the box, plus the
 *  one leaving at the top (opacity 0) so it fades instead of vanishing.
 *  Float-up = FLIP: measured from the stack's BOTTOM (the anchor), so a window
 *  resize never reads as movement. Runs only when the set of lines changes. */
export function SceneBubbles({ lines, calm = false, stage = false }) {
  const all = Array.isArray(lines) ? lines : [];
  const shown = all.slice(-(BUBBLE_SHOWN + 1));
  const n = shown.length;
  const boxRef = useRef(null);
  const nodes = useRef(new Map());
  const prev = useRef(new Map());
  const seeded = useRef(false);
  const sig = shown.map((l) => l.key).join('|');
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const bottom = box.getBoundingClientRect().bottom;
    const next = new Map();
    nodes.current.forEach((el, key) => {
      if (!el || !el.isConnected) return;
      if (typeof el.getAnimations === 'function') el.getAnimations().forEach((a) => a.cancel());
      next.set(key, bottom - el.getBoundingClientRect().top);
    });
    if (!calm && seeded.current) {
      next.forEach((rel, key) => {
        const el = nodes.current.get(key);
        if (!el || typeof el.animate !== 'function') return;
        const was = prev.current.get(key);
        const ease = { duration: FLOAT_MS, easing: 'cubic-bezier(.2,.8,.3,1)' };
        if (was === undefined) {
          el.animate([{ opacity: 0, transform: 'translateY(14px) scale(0.97)' }, { opacity: Number(el.getAttribute('data-op')) || 0, transform: 'none' }], ease);
        } else if (Math.abs(rel - was) > 0.5) {
          el.animate([{ transform: `translateY(${rel - was}px)` }, { transform: 'none' }], ease);
        }
      });
    }
    prev.current = next;
    seeded.current = true;
  }, [sig, calm]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!n) return null;
  return (
    <div ref={boxRef} data-scene-bubbles={stage ? 'stage' : 'picture'} role="log" aria-live="polite" aria-label="What people are saying here"
      style={{
        // the mockup's band: over the room's middle (clear of the moon above and
        // the box below), zig-zagging across ~40-62% of the picture's width
        ...(stage
          ? { position: 'relative', width: '100%', maxHeight: 'min(34vh, 300px)', flex: '0 1 auto', minHeight: 0 }
          : { position: 'absolute', left: '40%', right: '4%', bottom: '30%', maxHeight: '38%' }),
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'flex-start', gap: 8,
        overflow: 'hidden', pointerEvents: 'none', boxSizing: 'border-box',
        // a short band clips its oldest bubble at the top: fade that edge, so a
        // cut bubble reads as one fading out (never a sliced box). The fade sits
        // over an empty 22px pad: a band that fits is never dimmed.
        paddingTop: 22,
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, #000 22px)',
        maskImage: 'linear-gradient(to bottom, transparent 0, #000 22px)',
      }}>
      {shown.map((l, i) => {
        const rank = n - 1 - i; // 0 = the newest
        const op = rank < RANK_OPACITY.length ? RANK_OPACITY[rank] : 0;
        return (
          <div key={l.key} ref={(el) => { if (el) nodes.current.set(l.key, el); else nodes.current.delete(l.key); }}
            data-scene-bubble={l.ai ? 'ai' : 'person'} data-op={op} aria-hidden={op === 0 ? 'true' : undefined}
            style={{
              flex: '0 0 auto', marginLeft: `${bubbleNudge(l.key, stage ? 10 : 40)}%`,
              maxWidth: stage ? 'min(300px, 88%)' : 'min(250px, 70%)', boxSizing: 'border-box', padding: '7px 11px',
              borderRadius: '14px 14px 14px 4px', background: INK, border: `1px solid ${LINE}`,
              boxShadow: '0 2px 10px rgba(0,0,0,0.35)', color: PARCH, fontSize: stage ? 14.5 : 13.5, lineHeight: 1.35,
              textAlign: 'left', overflowWrap: 'anywhere', textShadow: 'none',
              opacity: op, transition: calm ? 'none' : 'opacity 0.45s ease',
            }}>
            <Name line={l} />{' '}
            {l.emote ? <i><Words text={bubbleText(l.text)} /></i> : <Words text={bubbleText(l.text)} />}
          </div>
        );
      })}
    </div>
  );
}

/** The message box on the picture: Enter or the gold button sends through the
 *  page's own send. A slash command stays in the chat box (its parser lives
 *  there), so a "/" line gets a note, never a literal post. */
export function SceneSay({ onSend, placeholder = 'Say something…', docked = false }) {
  const [text, setText] = useState('');
  const [state, setState] = useState(null); // null | 'sending' | 'error' | 'command'
  const [focused, setFocused] = useState(false); // our own ring: the inline shadow outranks the theme's focus glow
  const busy = useRef(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const clean = text.replace(/\s+/g, ' ').trim();
  const send = async () => {
    if (!clean || busy.current || !onSend) return;
    if (clean.startsWith('/')) { setState('command'); return; }
    busy.current = true;
    setState('sending');
    try {
      await onSend(clean.slice(0, SAY_MAX));
      if (alive.current) { setText(''); setState(null); }
    } catch (e) {
      if (alive.current) setState('error');
    } finally {
      busy.current = false;
    }
  };
  const note = state === 'error' ? "Didn't send. Try again." : (state === 'command' ? 'Commands go in the chat box.' : null);
  const off = !clean || state === 'sending';
  return (
    <div data-scene-say={docked ? 'dock' : 'picture'} onClick={stop} onPointerDown={stop} onPointerUp={stop} onPointerMove={stop}
      style={{
        ...(docked ? { position: 'relative', width: '100%' } : { position: 'absolute', left: '50%', bottom: 10, transform: 'translateX(-50%)', width: 'min(440px, calc(100% - 20px))' }),
        display: 'flex', alignItems: 'center', gap: 8, boxSizing: 'border-box', cursor: 'auto', touchAction: 'auto', userSelect: 'text', WebkitUserSelect: 'text',
      }}>
      {note && (
        <div data-scene-say-note="" aria-live="polite" style={{
          position: 'absolute', left: 0, right: 0, bottom: '100%', marginBottom: 6, textAlign: 'center',
          color: state === 'error' ? '#ff9f7a' : GOLD, fontSize: 12.5, fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,0.8)', pointerEvents: 'none',
        }}>{note}</div>
      )}
      <input type="text" aria-label="Say something to the room" placeholder={placeholder} maxLength={SAY_MAX}
        value={text} onChange={(e) => { setText(e.target.value); if (state === 'error' || state === 'command') setState(null); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !(e.nativeEvent && e.nativeEvent.isComposing)) { e.preventDefault(); send(); } }}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          flex: '1 1 auto', minWidth: 0, height: 44, boxSizing: 'border-box', padding: '0 14px', borderRadius: 999,
          border: `1px solid ${focused ? GOLD : LINE}`, background: '#0f0b08', color: PARCH, font: 'inherit', fontSize: 16, outline: 'none',
          boxShadow: focused ? '0 0 0 2px rgba(233,181,101,0.35), 0 4px 18px rgba(0,0,0,0.55)' : '0 4px 18px rgba(0,0,0,0.55)',
        }} />
      <button type="button" data-scene-send="" aria-label="Send" disabled={off} onClick={(e) => { e.stopPropagation(); send(); }}
        style={{
          width: 44, height: 44, flex: '0 0 auto', padding: 0, borderRadius: 999, border: 'none', boxSizing: 'border-box',
          background: '#ffc864', color: '#2a1c08', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: off ? 'default' : 'pointer', opacity: off ? 0.6 : 1, textShadow: 'none', boxShadow: '0 4px 18px rgba(0,0,0,0.45)',
        }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12l16-8-6 16-2.5-6.5z" /></svg>
      </button>
    </div>
  );
}

/** "Show all N messages": opens the whole list, in the scene. */
export function ShowAllButton({ count, onOpen, docked = false, open = false }) {
  if (!(count > 0) || !onOpen) return null;
  return (
    <button type="button" data-scene-show-all={count} aria-expanded={open ? 'true' : 'false'} onClick={(e) => { e.stopPropagation(); onOpen(); }}
      style={{
        alignSelf: 'center', height: 34, padding: '0 14px', borderRadius: 999, boxSizing: 'border-box', cursor: 'pointer',
        border: `1px solid ${LINE}`, background: docked ? INK : 'transparent', color: GOLD,
        font: 'inherit', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, textShadow: 'none',
      }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z" /></svg>
      {count === 1 ? 'Show 1 message' : `Show all ${count} messages`}
    </button>
  );
}

const timeOf = (ms) => {
  try { return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch (e) { return ''; }
};

/** Every line since the event started, oldest first, in a panel over the
 *  scene (Nina 09-26: "a message list in the scene"). It opens at the newest
 *  line and follows new ones while you are at the bottom. */
export function SceneLog({ lines, onClose, docked = false }) {
  const all = Array.isArray(lines) ? lines : [];
  const listRef = useRef(null);
  const atBottom = useRef(true);
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [all.length]);
  return (
    <section data-scene-log={docked ? 'dock' : 'picture'} aria-label="All messages here"
      onClick={stop} onPointerDown={stop} onPointerUp={stop} onPointerMove={stop}
      style={{
        ...(docked
          ? { position: 'relative', width: '100%', maxHeight: 'min(52vh, 460px)', flex: '0 1 auto', minHeight: 0, pointerEvents: 'auto' }
          : { position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '10%', bottom: 62, width: 'min(520px, 64%)' }),
        display: 'flex', flexDirection: 'column', boxSizing: 'border-box', borderRadius: 14, overflow: 'hidden',
        background: 'rgba(16,11,7,0.95)', border: `1px solid ${LINE}`, boxShadow: '0 8px 30px rgba(0,0,0,0.6)',
        cursor: 'auto', touchAction: 'auto', textAlign: 'left', color: PARCH, textShadow: 'none',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 8px 6px 14px', borderBottom: `1px solid ${LINE}`, flex: '0 0 auto' }}>
        <span style={{ color: GOLD, fontSize: 13.5, fontWeight: 700 }}>All messages</span>
        <button type="button" data-scene-log-close="" aria-label="Close the messages" title="Close" onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}
          style={{
            width: 34, height: 34, padding: 0, borderRadius: 999, border: `1px solid ${LINE}`, background: '#2c2117', color: PARCH,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxSizing: 'border-box', textShadow: 'none',
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>
      <div ref={listRef} data-scene-log-list=""
        onScroll={(e) => { const el = e.currentTarget; atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24; }}
        style={{ overflowY: 'auto', overscrollBehavior: 'contain', padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 9, flex: '1 1 auto', minHeight: 0 }}>
        {all.map((l) => (
          <div key={l.key} data-scene-log-line={l.ai ? 'ai' : 'person'} style={{ fontSize: 14, lineHeight: 1.4, overflowWrap: 'anywhere' }}>
            <Name line={l} />
            <span style={{ color: 'rgba(217,205,187,0.55)', fontSize: 11.5, marginLeft: 6 }}>{timeOf(l.at)}</span>
            <div>{l.emote ? <i><Words text={l.text} /></i> : <Words text={l.text} />}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
